import type { Address, ContactPoint, HumanName } from '@medplum/fhirtypes'
import { Participant } from '@seen/core/models/patients/participant'
import type { AddressSummary } from '@seen/core/models/profile'
import { demoNumber } from '#src/demo-mode/demoNumbers.ts'
import { recordHiddenName } from '#src/demo-mode/scrubKnownNames.ts'

const recorded = new WeakSet<object>()

// Every Participant read from FHIR is built as this class while demo mode is
// on (installDemoModeIfStored). It hides PHI in the getters screens read; the raw
// resource, toFhirResource() and createReference() keep the real values.
// Each accessor with a setter on Participant keeps that setter.
export class RedactedParticipant extends Participant {
  private get demoNumber(): number {
    const id = this.resource.id ?? ''
    if (!recorded.has(this.resource)) {
      recorded.add(this.resource)
      recordHiddenName(id, [
        super.name,
        `${super.firstName} ${super.lastName}`,
        super.firstName,
        super.lastName,
        super.nickname,
      ])
    }
    return demoNumber(id)
  }

  override get name(): string {
    return this.isDeleted ? super.name : `Participant ${this.demoNumber}`
  }

  override get nameLastFirstMiddleInitial(): string {
    return this.name
  }

  override get firstName(): string {
    return this.isDeleted ? super.firstName : 'Participant'
  }

  override get lastName(): string {
    return this.isDeleted ? super.lastName : String(this.demoNumber)
  }

  override get firstNameOfficialOrFallback(): string | undefined {
    return this.firstName
  }

  override get lastNameOfficialOrFallback(): string | undefined {
    return this.lastName
  }

  override get nickname(): string | undefined {
    return undefined
  }

  override get initials(): string {
    return `P${this.demoNumber}`
  }

  override get names(): HumanName[] {
    return [{ given: [this.firstName], family: this.lastName }]
  }

  override get seenMRN(): string | undefined {
    return `DEMO-${String(this.demoNumber).padStart(4, '0')}`
  }

  override set seenMRN(value: string | undefined) {
    super.seenMRN = value
  }

  override get ssn(): string | undefined {
    return undefined
  }

  override set ssn(value: string | undefined) {
    super.ssn = value
  }

  override get medicareId(): string | undefined {
    return undefined
  }

  override set medicareId(value: string | undefined) {
    super.medicareId = value
  }

  override get medicaidId(): string | undefined {
    return undefined
  }

  override set medicaidId(value: string | undefined) {
    super.medicaidId = value
  }

  // `age` reads the resource, so it still shows.
  override get birthDate(): string | undefined {
    return undefined
  }

  override set birthDate(value: string | undefined) {
    super.birthDate = value
  }

  override get birthDateFormatted(): string | undefined {
    return undefined
  }

  override get primaryPhoneNumber(): string | undefined {
    return `(555) 555-${String(this.demoNumber % 10000).padStart(4, '0')}`
  }

  override get primaryEmail(): string | undefined {
    return `participant${this.demoNumber}@example.com`
  }

  override get primaryFaxNumber(): string | undefined {
    return undefined
  }

  override get contactPoints(): ContactPoint[] {
    return []
  }

  override get contactMethods(): ContactPoint[] {
    return []
  }

  override set contactMethods(methods: ContactPoint[]) {
    super.contactMethods = methods
  }

  override get telecomSummary(): Participant['telecomSummary'] {
    return []
  }

  override get primaryAddress(): Address | undefined {
    return undefined
  }

  override get homeAddress(): Address | undefined {
    return undefined
  }

  override get address(): Address[] {
    return []
  }

  override set address(address: Address[]) {
    super.address = address
  }

  override get addresses(): Address[] {
    return []
  }

  override set addresses(addresses: Address[]) {
    super.addresses = addresses
  }

  override get addressSummary(): AddressSummary {
    return {}
  }

  override get photoUrl(): string | undefined {
    return undefined
  }
}
